import { createSlice } from "@reduxjs/toolkit";
import type {
  SlaSubmission,
  SlaSubmissionTag,
} from "../../../domain/sla/model";

type LoadSubmissionsState = {
  loading: boolean;
  data: SlaSubmission[];
  error: unknown;
  loadingById: Record<string, boolean>;
  errorById: Record<string, unknown>;
  tagsSavedDuringDetailLoadById: Record<string, SlaSubmissionTag[]>;
  tagsSavedDuringListLoadById: Record<string, SlaSubmissionTag[]>;
};

type SubmissionError = { id: string; error: unknown };

const initialState: LoadSubmissionsState = {
  loading: false,
  data: [],
  error: null,
  loadingById: {},
  errorById: {},
  tagsSavedDuringDetailLoadById: {},
  tagsSavedDuringListLoadById: {},
};

const mergeSavedTags = (
  incoming: SlaSubmissionTag[],
  saved: SlaSubmissionTag[] = [],
) =>
  Array.from(
    new Map([...incoming, ...saved].map((tag) => [tag.tagId, tag])).values(),
  );

const dataSlice = createSlice({
  name: "load-submissions",
  initialState: initialState,
  reducers: {
    initLoadSubmissions: () => {
      return { ...initialState };
    },
    startLoadSubmissions: (state) => {
      state.loading = true;
      state.data = [];
      state.error = null;
      state.loadingById = {};
      state.errorById = {};
      state.tagsSavedDuringDetailLoadById = {};
      state.tagsSavedDuringListLoadById = {};
    },
    successLoadSubmissions: (state, action: { payload: SlaSubmission[] }) => {
      state.loading = false;
      state.data = action.payload.map((submission) => ({
        ...submission,
        tags: mergeSavedTags(
          submission.tags,
          state.tagsSavedDuringListLoadById[submission.id],
        ),
      }));
      state.tagsSavedDuringListLoadById = {};
    },
    errorLoadSubmissions: (state, action: { payload: unknown }) => {
      state.loading = false;
      state.error = action.payload;
      state.tagsSavedDuringListLoadById = {};
    },
    startLoadSubmission: (state, action: { payload: string }) => {
      const id = action.payload;
      if (id) {
        state.loadingById[id] = true;
        state.tagsSavedDuringDetailLoadById[id] = [];
      }
    },
    errorLoadSubmission: (state, action: { payload: SubmissionError }) => {
      const { id, error } = action.payload || {};
      if (id) {
        state.loadingById[id] = false;
        state.errorById[id] = error;
        delete state.tagsSavedDuringDetailLoadById[id];
      }
    },
    // Upsert a single submission (used when expanding a row to fetch latest data)
    upsertSubmission: (state, action: { payload: SlaSubmission }) => {
      const incoming = action.payload;
      if (!incoming?.id) return;
      // Confirmed tag writes are newer than this pending detail snapshot.
      const savedTags = state.tagsSavedDuringDetailLoadById[incoming.id] ?? [];
      const submission = {
        ...incoming,
        tags: mergeSavedTags(incoming.tags, savedTags),
      };
      const idx = state.data.findIndex((s) => s.id === submission.id);
      if (idx >= 0) {
        state.data[idx] = submission; // replace with fresh copy
      } else {
        state.data.push(submission);
      }
      state.loadingById[submission.id] = false;
      state.errorById[submission.id] = null;
      delete state.tagsSavedDuringDetailLoadById[submission.id];
    },
    updateSubmissionTags: (
      state,
      action: { payload: { answerId: string; tags: SlaSubmissionTag[] } },
    ) => {
      const { answerId, tags } = action.payload;
      if (state.loading) {
        state.tagsSavedDuringListLoadById[answerId] = mergeSavedTags(
          state.tagsSavedDuringListLoadById[answerId] ?? [],
          tags,
        );
      }
      if (state.loadingById[answerId]) {
        state.tagsSavedDuringDetailLoadById[answerId] = mergeSavedTags(
          state.tagsSavedDuringDetailLoadById[answerId] ?? [],
          tags,
        );
      }
      const submission = state.data.find((item) => item.id === answerId);
      if (submission) submission.tags = mergeSavedTags(submission.tags, tags);
    },
    // Remove a submission from the list
    removeSubmission: (state, action: { payload: string }) => {
      const submissionId = action.payload;
      if (!submissionId) return;
      state.data = state.data.filter((s) => s.id !== submissionId);
      delete state.loadingById[submissionId];
      delete state.errorById[submissionId];
      delete state.tagsSavedDuringDetailLoadById[submissionId];
      delete state.tagsSavedDuringListLoadById[submissionId];
    },
  },
});

export const {
  initLoadSubmissions,
  startLoadSubmissions,
  successLoadSubmissions,
  errorLoadSubmissions,
  startLoadSubmission,
  errorLoadSubmission,
  upsertSubmission,
  updateSubmissionTags,
  removeSubmission,
} = dataSlice.actions;

export default dataSlice.reducer;
